import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormikContext } from 'formik'
import { useNewTranslation } from 'hooks/index'
import { AiReviewContainer } from './style'
import RoundButton from './RoundButton'
import Icon from 'particles/Icon'
import Text from 'particles/Text'
import {
    SubmissionProgressProps,
    selectSelectedCampaignSummary,
    selectSelectedSubmissionView,
    CaptionReviewProps,
    selectForceCaptionReviewSuccessProgressUuids,
    setForceCaptionReviewSuccessProgressUuids,
    selectRewriteCaptionReviewProgressUuids,
    selectRetryCaptionReviewProgressUuids,
    setRewriteCaptionReviewProgressUuids,
    setRetryCaptionReviewProgressUuids,
    setInfoToastOpen,
    setInfoToastMessage,
} from 'state/review'
import { selectCampaign } from 'state'
import { reviewCaption } from 'state/submissions'
import { FormikValues } from 'components/Submissions/Review/Submissions/Verify/ReviewSubmission'
import RectangleButton from 'particles/Button/RectangleButton'

interface AiReviewProps {
    reviewMode: 'Approval' | 'RequestEdit'
    submissionProgress: SubmissionProgressProps
}

const AiReview: React.FC<AiReviewProps> = ({ reviewMode, submissionProgress }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const { values, setFieldValue } = useFormikContext<FormikValues>()
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const selectedCampaignUuid = campaignSummary?.uuid ?? ''
    const campaign = useSelector(selectCampaign(selectedCampaignUuid))
    const isAiCaptionReviewEnabled = campaign.brand.team?.is_ai_caption_review_enabled
    const rewriteProgressUuids = useSelector(selectRewriteCaptionReviewProgressUuids)
    const retryProgressUuids = useSelector(selectRetryCaptionReviewProgressUuids)
    const forceCaptionReviewSuccessProgressUuids = useSelector(selectForceCaptionReviewSuccessProgressUuids)
    const captionReviewStatus = submissionProgress.data?.caption_review_status
    const captionReview = submissionProgress.data?.caption_review
    const showSuccessResult =
        (captionReviewStatus == 'SUCCESS' && captionReview?.final_verdict == 'Approve') ||
        forceCaptionReviewSuccessProgressUuids.includes(submissionProgress.uuid)
    const showEditResult =
        captionReviewStatus == 'SUCCESS' &&
        captionReview?.final_verdict == 'Request Edit' &&
        !forceCaptionReviewSuccessProgressUuids.includes(submissionProgress.uuid)
    const showErrorResult = captionReviewStatus == 'FAILED'
    const showReviewBlock = isAiCaptionReviewEnabled && (showSuccessResult || showEditResult || showErrorResult)
    const showRetryButton = isAiCaptionReviewEnabled && reviewMode === 'Approval' && showReviewBlock
    const disableRetryButton =
        rewriteProgressUuids.includes(submissionProgress.uuid) || retryProgressUuids.includes(submissionProgress.uuid)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)

    const updateLoadingCallback = (uuid: string) => {
        const updatedList = retryProgressUuids.filter(submissionUuid => submissionUuid != uuid)
        dispatch(setRetryCaptionReviewProgressUuids(updatedList))
    }
    const retrySuccessCallback = () => {
        dispatch(
            setForceCaptionReviewSuccessProgressUuids(
                forceCaptionReviewSuccessProgressUuids.filter(uuid => uuid != submissionProgress.uuid),
            ),
        )
    }
    return (
        <AiReviewContainer>
            {showRetryButton && (
                <RoundButton
                    icon="sparkles"
                    label={newT('button.checkForIssueTitle')}
                    loadingText={newT('loadingIndicator.checkingLabel')}
                    isLoading={retryProgressUuids.includes(submissionProgress.uuid)}
                    isDisabled={disableRetryButton}
                    onClick={e => {
                        e.preventDefault()
                        if (selectedSubmissionView) {
                            dispatch(
                                setRetryCaptionReviewProgressUuids([...retryProgressUuids, submissionProgress.uuid]),
                            )
                            dispatch(
                                reviewCaption({
                                    selectedSubmissionView: selectedSubmissionView,
                                    submissionProgressUuid: submissionProgress.uuid,
                                    caption: values.data.caption,
                                    updateLoadingCallback,
                                    retrySuccessCallback,
                                }),
                            )
                        }
                    }}
                />
            )}
            {showReviewBlock && (
                <div className="review-status">
                    <div className="icon-block">
                        <div>
                            <Icon name="sparkles" color={'irisPurple'} />
                        </div>
                        <div className="review-block">
                            <div className="review-title-block">
                                <Text scale="caption700">{newT('submission.review.aiReviewTitle')}</Text>
                                {showSuccessResult && (
                                    <Text scale="caption700" color="positive">
                                        {newT('submission.review.aiReviewStatus.suggestApprove')}
                                    </Text>
                                )}
                                {showEditResult && (
                                    <Text scale="caption700" color="warning">
                                        {newT('submission.review.aiReviewStatus.suggestEdited')}
                                    </Text>
                                )}
                                {showErrorResult && (
                                    <Text scale="caption700" color="error">
                                        {newT('submission.review.aiReviewStatus.reviewFail')}
                                    </Text>
                                )}
                            </div>
                            {showEditResult &&
                                captionReview &&
                                captionReview.edit_reasons.map((reason: CaptionReviewProps, index: number) => (
                                    <div key={index} className="edit-reason">
                                        <Text scale="caption500" color="primary">
                                            &bull;
                                        </Text>
                                        <Text scale="caption500" color="primary">
                                            {reason}
                                        </Text>
                                    </div>
                                ))}
                            {showEditResult && reviewMode === 'Approval' && (
                                <div>
                                    <RectangleButton
                                        size="s"
                                        icon="pencilCircle"
                                        label={newT('button.rewriteWithAI')}
                                        form="text"
                                        color="primary"
                                        onClick={e => {
                                            e.preventDefault()
                                            dispatch(
                                                setForceCaptionReviewSuccessProgressUuids([
                                                    ...forceCaptionReviewSuccessProgressUuids,
                                                    submissionProgress.uuid,
                                                ]),
                                            )
                                            setFieldValue('data.caption', captionReview.suggested_caption)
                                            dispatch(
                                                setRewriteCaptionReviewProgressUuids([
                                                    ...rewriteProgressUuids,
                                                    submissionProgress.uuid,
                                                ]),
                                            )
                                        }}
                                    />
                                </div>
                            )}
                            {showEditResult && reviewMode === 'RequestEdit' && (
                                <div>
                                    <RectangleButton
                                        size="s"
                                        icon="copy"
                                        label={newT('button.copySuggestion')}
                                        form="text"
                                        color="primary"
                                        onClick={e => {
                                            e.preventDefault()
                                            const copyText = captionReview.edit_reasons.join('\n')
                                            navigator.clipboard.writeText(copyText)
                                            dispatch(setInfoToastMessage(newT('toast.copiedSuccessfully')))
                                            dispatch(setInfoToastOpen(true))
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </AiReviewContainer>
    )
}
export default AiReview
